/**
 * Error thrown when a storage operation exceeds quota limits even after
 * attempting to free space using a purge strategy.
 *
 * This error indicates that quota pressure was detected, a corrective
 * purge was attempted, and the operation still could not be completed.
 */
class QuotaExceededAfterPurgeError extends Error {
  /**
   * @param {Object} params Constructor params
   * @param {any} [params.context]
   * Arbitrary contextual information related to the failed operation.
   * This data is not interpreted by the error itself and is intended
   * solely for debugging and logging purposes.
   *
   * @param {Error} params.originalError
   * The original quota exceeded error thrown by the storage implementation.
   *
   * @param {function(): void} params.operation
   * The operation that was being executed when the error occurred.
   */
  constructor({ context, originalError, operation }) {
    super(
      'Storage quota exceeded after purge; insufficient space to complete write'
    );

    this.name = 'QuotaExceededAfterPurgeError';
    this.context = context;
    this.operation = operation;
    this.originalError = originalError;
  }
}

/**
 * Determines whether a thrown exception indicates that a storage quota has been
 * exceeded.
 *
 * Browsers throw a `DOMException` when a `localStorage` or `sessionStorage`
 * operation exceeds the available space.
 *
 * @param {any} e The exception object thrown by `setItem` or similar.
 * It may be:
 *   - A DOMException from storage APIs
 *   - Any other object if thrown by custom storage implementations
 *
 * @returns {boolean} `true` if the exception corresponds to a quota exceeded
 * error, otherwise `false`.
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/DOMException
 * @link https://developer.mozilla.org/en-US/docs/Web/API/QuotaExceededError
 */
function isQuotaExceededError(e) {
  if (!e) return false;

  return e instanceof DOMException && e.name === 'QuotaExceededError';
}
/**
 * Executes a storage operation and, if a quota exceeded error occurs,
 * attempts to free space using a provided purge strategy and retries
 * the operation once.
 *
 * This helper provides a controlled execution flow for storage write
 * operations that may fail due to quota limits.
 *
 * Execution flow:
 * 1. Executes the given operation.
 * 2. If the operation throws a quota exceeded error:
 *    - Invokes the provided `purge` function to attempt freeing space.
 *    - Retries the operation once.
 * 3. If the retry also fails with a quota exceeded error:
 *    - Throws a `QuotaExceededAfterPurgeError` enriched with context.
 * 4. Any non-quota errors are rethrown immediately.
 *
 * Important notes:
 * - This function does NOT guarantee success.
 * - The purge strategy may remove zero or more entries.
 * - Only a single retry is performed to avoid infinite loops.
 * - This helper is synchronous and only handles errors thrown synchronously.
 *
 * @param {Object} params
 *
 * @param {function(): void} params.operation
 * Function that performs the storage operation (for example, `setItem`).
 * It must throw synchronously on failure.
 *
 * @param {function(): void} [params.purge]
 * Function that attempts to free space (for example, by removing expired
 * or evictable entries). It may remove zero or more entries.
 *
 * @param {any} [params.context]
 * Arbitrary contextual information describing the operation being executed
 * (such as storage key, value metadata, purge strategy, or storage type).
 * This data is attached to the thrown error for debugging and logging.
 *
 * @throws {Error}
 * - Rethrows any non-quota error thrown by `operation`.
 * - Throws `QuotaExceededAfterPurgeError` if the retry also fails due to
 *   a quota exceeded error.
 */
function executeWithPurgeAndRetryIfQuotaExceeded({
  operation,
  purge,
  context,
}) {
  try {
    operation();
  } catch (err) {
    if (!isQuotaExceededError(err)) {
      throw err;
    }

    // Attempt to free space before retrying
    if (typeof purge === 'function') {
      purge();
    }

    try {
      operation(); // retry once
    } catch (retryErr) {
      if (isQuotaExceededError(retryErr)) {
        throw new QuotaExceededAfterPurgeError({
          originalError: retryErr,
          operation,
          context,
        });
      }
      throw retryErr;
    }
  }
}

module.exports = {
  executeWithPurgeAndRetryIfQuotaExceeded,
  QuotaExceededAfterPurgeError,
};
